import { useState, useCallback, useEffect, useRef } from 'react';

/**
 * Storage key pattern matching useTablePreferences/useSortPreference for compatibility
 */
const getStorageKey = (tableId: string) => `table-prefs-${tableId}`;

type FilterState = Record<string, string[]>;

interface UseFilterPreferenceOptions {
  tableId: string;
}

interface UseFilterPreferenceReturn {
  filters: FilterState;
  setFilters: (filters: FilterState) => void;
  clearFilters: () => void;
}

/**
 * Load filter preference from localStorage
 * Compatible with useTablePreferences storage format
 */
function loadFilterPreference(tableId: string): FilterState {
  try {
    const stored = localStorage.getItem(getStorageKey(tableId));
    if (!stored) {
      return {};
    }

    const parsed = JSON.parse(stored);
    if (parsed.filters && typeof parsed.filters === 'object') {
      return parsed.filters;
    }

    return {};
  } catch (e) {
    console.warn(`Failed to load filter preference for ${tableId}:`, e);
    return {};
  }
}

/**
 * Save filter preference to localStorage
 * Compatible with useTablePreferences storage format
 */
function saveFilterPreference(tableId: string, filters: FilterState): void {
  try {
    const key = getStorageKey(tableId);
    const existing = localStorage.getItem(key);
    let data: any = {};

    if (existing) {
      try {
        data = JSON.parse(existing);
      } catch {
        data = {};
      }
    }

    // Update only the filters field, preserving other preferences (like sort)
    data.filters = filters;

    localStorage.setItem(key, JSON.stringify(data));
  } catch (e) {
    console.warn(`Failed to save filter preference for ${tableId}:`, e);
  }
}

/**
 * Hook for managing filter preference with localStorage persistence.
 * Use this for server-side filtering scenarios where the parent component
 * controls the filter state but needs it persisted across page refreshes.
 *
 * Compatible with useTablePreferences/useSortPreference storage format.
 *
 * @example
 * ```tsx
 * const { filters, setFilters, clearFilters } = useFilterPreference({
 *   tableId: 'cars-table-all',
 * });
 *
 * // Pass to CarsTable for server-side filtering
 * <CarsTable
 *   columnFilters={filters}
 *   onFiltersChange={setFilters}
 * />
 * ```
 */
export function useFilterPreference({
  tableId,
}: UseFilterPreferenceOptions): UseFilterPreferenceReturn {
  // Initialize state from localStorage
  const [filters, setFiltersState] = useState<FilterState>(() =>
    loadFilterPreference(tableId)
  );

  // Track if this is the initial mount to avoid unnecessary saves
  const isInitialMount = useRef(true);
  const prevTableIdRef = useRef(tableId);

  // Re-load from localStorage when tableId changes
  useEffect(() => {
    if (prevTableIdRef.current !== tableId) {
      prevTableIdRef.current = tableId;
      setFiltersState(loadFilterPreference(tableId));
    }
  }, [tableId]);

  // Persist to localStorage when filters change (skip initial mount)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    saveFilterPreference(tableId, filters);
  }, [tableId, filters]);

  // Handle filter changes
  const setFilters = useCallback((newFilters: FilterState) => {
    setFiltersState(newFilters);
  }, []);

  // Clear all filters
  const clearFilters = useCallback(() => {
    setFiltersState({});
  }, []);

  return {
    filters,
    setFilters,
    clearFilters,
  };
}
